/* eslint-disable max-lines */
import type { IntlShape } from 'react-intl';

import type { CloudResource } from '@/lib/queries/resource';
import type { CoverageResource } from '@/lib/queries/coverage';

export const IAM_ROLE = {
  service: 'iam',
  resourceType: 'role',
} as const;

export const IAM_USER = {
  service: 'iam',
  resourceType: 'user',
} as const;

export const IAM_POLICY = {
  service: 'iam',
  resourceType: 'policy',
} as const;

export const IAM_GROUP = {
  service: 'iam',
  resourceType: 'group',
} as const;

export const EC2_ELASTIC_IP = {
  service: 'ec2',
  resourceType: 'elastic-ip',
} as const;

export const EC2_IMAGE = {
  service: 'ec2',
  resourceType: 'image',
} as const;

export const EC2_INSTANCE = {
  service: 'ec2',
  resourceType: 'instance',
} as const;

export const EC2_INTERNET_GATEWAY = {
  service: 'ec2',
  resourceType: 'internet-gateway',
} as const;

export const EC2_KEY_PAIR = {
  service: 'ec2',
  resourceType: 'key-pair',
} as const;

export const EC2_LAUNCH_TEMPLATE = {
  service: 'ec2',
  resourceType: 'launch-template',
} as const;

export const EC2_LOAD_BALANCER = {
  service: 'ec2',
  resourceType: 'load-balancer',
} as const;

export const EC2_NETWORK_ACL = {
  service: 'ec2',
  resourceType: 'network-acl',
} as const;

export const EC2_NETWORK_INTERFACE = {
  service: 'ec2',
  resourceType: 'network-interface',
} as const;

export const EC2_PLACEMENT_GROUP = {
  service: 'ec2',
  resourceType: 'placement-group',
} as const;

export const EC2_ROUTE_TABLE = {
  service: 'ec2',
  resourceType: 'route-table',
} as const;

export const EC2_SECURITY_GROUP = {
  service: 'ec2',
  resourceType: 'security-group',
} as const;

export const EC2_SNAPSHOT = {
  service: 'ec2',
  resourceType: 'snapshot',
} as const;

export const EC2_SUBNET = {
  service: 'ec2',
  resourceType: 'subnet',
} as const;

export const EC2_VPC = {
  service: 'ec2',
  resourceType: 'vpc',
} as const;

export const EC2_VOLUME = {
  service: 'ec2',
  resourceType: 'volume',
} as const;

export const LAMBDA_ALIAS = {
  service: 'lambda',
  resourceType: 'alias',
} as const;

export const LAMBDA_EVENT_SOURCE_MAPPING = {
  service: 'lambda',
  resourceType: 'event-source-mapping',
} as const;

export const LAMBDA_FUNCTION = {
  service: 'lambda',
  resourceType: 'function',
} as const;

export const LAMBDA_LAYER = {
  service: 'lambda',
  resourceType: 'layer',
} as const;

export const LAMBDA_VERSION = {
  service: 'lambda',
  resourceType: 'version',
} as const;

export const RDS_DB_CLUSTER = {
  service: 'rds',
  resourceType: 'cluster',
} as const;

export const RDS_DB_CLUSTER_PARAMETER_GROUP = {
  service: 'rds',
  resourceType: 'cluster-pg',
} as const;

export const RDS_DB_INSTANCE = {
  service: 'rds',
  resourceType: 'db',
} as const;

export const RDS_DB_PARAMETER_GROUP = {
  service: 'rds',
  resourceType: 'pg',
} as const;

export const RDS_DB_PROXY = {
  service: 'rds',
  resourceType: 'db-proxy',
} as const;

export const RDS_DB_SECURITY_GROUP = {
  service: 'rds',
  resourceType: 'secgrp',
} as const;

export const RDS_DB_SUBNET_GROUP = {
  service: 'rds',
  resourceType: 'subgrp',
} as const;

export const RDS_OPTION_GROUP = {
  service: 'rds',
  resourceType: 'og',
} as const;

export const ROUTE53_CIDR = {
  service: 'route53',
  resourceType: 'cidr',
} as const;

export const ROUTE53_HEALTH_CHECK = {
  service: 'route53',
  resourceType: 'health-check',
} as const;

export const ROUTE53_HOSTED_ZONE = {
  service: 'route53',
  resourceType: 'hostedzone',
} as const;

export const ROUTE53_QUERY_LOGGING_CONFIG = {
  service: 'route53',
  resourceType: 'query-logging-config',
} as const;

export const ROUTE53_REUSABLE_DELEGATION_SET = {
  service: 'route53',
  resourceType: 'delegationset',
} as const;

export const ROUTE53_TRAFFIC_POLICY = {
  service: 'route53',
  resourceType: 'trafficpolicy',
} as const;

export const ROUTE53_TRAFFIC_POLICY_INSTANCE = {
  service: 'route53',
  resourceType: 'trafficpolicyinstance',
} as const;

export const S3_ACCESS_POINT = {
  service: 's3',
  resourceType: 'access-point',
} as const;

export const S3_BUCKET = {
  service: 's3',
  resourceType: 'bucket',
} as const;

export type HandledCloudResource = CoverageResource & ResourceDefs;

export const RESOURCE_DEFS = [
  IAM_ROLE,
  IAM_USER,
  IAM_POLICY,
  IAM_GROUP,
  EC2_ELASTIC_IP,
  EC2_IMAGE,
  EC2_INSTANCE,
  EC2_INTERNET_GATEWAY,
  EC2_KEY_PAIR,
  EC2_LAUNCH_TEMPLATE,
  EC2_LOAD_BALANCER,
  EC2_NETWORK_ACL,
  EC2_NETWORK_INTERFACE,
  EC2_PLACEMENT_GROUP,
  EC2_ROUTE_TABLE,
  EC2_SECURITY_GROUP,
  EC2_SNAPSHOT,
  EC2_SUBNET,
  EC2_VPC,
  EC2_VOLUME,
  LAMBDA_ALIAS,
  LAMBDA_EVENT_SOURCE_MAPPING,
  LAMBDA_FUNCTION,
  LAMBDA_LAYER,
  LAMBDA_VERSION,
  RDS_DB_CLUSTER,
  RDS_DB_CLUSTER_PARAMETER_GROUP,
  RDS_DB_INSTANCE,
  RDS_DB_PARAMETER_GROUP,
  RDS_DB_PROXY,
  RDS_DB_SECURITY_GROUP,
  RDS_DB_SUBNET_GROUP,
  RDS_OPTION_GROUP,
  ROUTE53_CIDR,
  ROUTE53_HEALTH_CHECK,
  ROUTE53_HOSTED_ZONE,
  ROUTE53_QUERY_LOGGING_CONFIG,
  ROUTE53_REUSABLE_DELEGATION_SET,
  ROUTE53_TRAFFIC_POLICY,
  ROUTE53_TRAFFIC_POLICY_INSTANCE,
  S3_ACCESS_POINT,
  S3_BUCKET,
] as const;

export type ResourceDefs = (typeof RESOURCE_DEFS)[number];

export const getTranslation = (
  r: ResourceDefs,
  t: IntlShape['formatMessage'],
  emoji: boolean,
): string => {
  const fieldName = emoji ? 'emojiResourceTypes' : 'resourceTypes';
  switch (r.service) {
    case 'lambda':
      return t({ id: `coverage.${fieldName}.lambda.${r.resourceType}` });
    case 'rds':
      return t({ id: `coverage.${fieldName}.rds.${r.resourceType}` });
    case 'iam':
      return t({ id: `coverage.${fieldName}.iam.${r.resourceType}` });
    case 'ec2':
      return t({ id: `coverage.${fieldName}.ec2.${r.resourceType}` });
    case 'route53':
      return t({ id: `coverage.${fieldName}.route53.${r.resourceType}` });
    case 's3':
      return t({ id: `coverage.${fieldName}.s3.${r.resourceType}` });
  }
};

export const isResourceType = (
  value: CloudResource | undefined,
): value is HandledCloudResource =>
  RESOURCE_DEFS.find(
    ({ service, resourceType }) =>
      value?.service === service && value.resourceType === resourceType,
  ) !== undefined;
